/* =========================================================
   CHAGUAL — Header offset bridge

   L-02 (Header): este archivo sigue unificado solo como bridge temporal,
   pero con fronteras explícitas por superficie:

   A) Shared header cover publisher
      - publica --chagual-header-cover y --chagual-header-offset
      - no decide layout de dominio; solo expone medición compartida

   B) Collection offset bridge
      - solo actúa en las cinco colecciones con filtros propios
      - calcula --chagual-header-push para evitar doble offset en PLP

   C) PDP medidas anchor bridge
      - solo actúa sobre #pdp-medidas y links .pdp-measures-inline__a
      - fuerza scroll con offset y apertura de <details>

   La separación final por archivo queda para fases posteriores.
   ========================================================= */

(function () {
  'use strict';

  var ROOT = document.documentElement;
  var SELECTORS = {
    header: '#header, header',
    collectionSection: '.shopify-section--collection-template.under-menu',
    ownedCollectionSurfaces: [
      '[data-ch-filter-count-owner="maceteros"]',
      '[data-ch-filter-count-owner="muebles"]',
      '[data-ch-filter-count-owner="sale-root"]',
      '[data-ch-filter-count-owner="sale-maceteros"]',
      '[data-ch-filter-count-owner="sale-muebles"]'
    ],
    pdpTarget: '#pdp-medidas',
    pdpLink: 'a.pdp-measures-inline__a, a[href="#pdp-medidas"]'
  };

  function px(n) {
    return Math.max(0, Math.round(n || 0)) + 'px';
  }

  function getHeaderEl() {
    return document.querySelector(SELECTORS.header);
  }

  function getHeaderCoverPx() {
    var header = getHeaderEl();
    if (!header) return 0;

    var cs = window.getComputedStyle(header);
    var pos = cs.position;
    if (pos !== 'fixed' && pos !== 'sticky') return 0;

    return header.getBoundingClientRect().height || header.offsetHeight || 0;
  }

  function elHeight(el) {
    if (!el) return 0;
    var rect = el.getBoundingClientRect();
    return rect.height || el.offsetHeight || 0;
  }

  /* Alto de la barra fija en móvil: solo promo + top-bar de #header
     (NO el drawer del menú abierto). En desktop #header es display:none → 0. */
  function getMobileHeaderHeightPx() {
    var header = document.querySelector('#header');
    if (!header) return 0;

    var h = elHeight(header.querySelector('.promo-banner')) +
            elHeight(header.querySelector('.top-bar'));

    if (!h && !document.body.classList.contains('is-active')) {
      h = elHeight(header);
    }
    return h;
  }

  /* Alto de la barra sticky en desktop = .main-nav__wrapper (lo que Headhesive
     clona). En móvil suele estar oculta → 0. */
  function getDesktopNavHeightPx() {
    return elHeight(
      document.querySelector('[data-desktop-header] .main-nav__wrapper') ||
      document.querySelector('.main-nav__wrapper')
    );
  }

  /* El header está configurado como fijo/sticky (setting fixed_header). */
  function isStickyHeaderEnabled() {
    return !!(
      document.querySelector('#header.mobile_nav-fixed--true') ||
      document.querySelector('.header.header-fixed--true')
    );
  }

  /* Alto REAL que hay que reservar para que las anclas no queden tapadas. */
  function getScrollOffsetPx() {
    // PDP ya compensa sus anclas con scroll-margin-top propio → no duplicar.
    if (document.body && document.body.classList.contains('product')) return 0;
    // Solo reservar si el header realmente es fijo/sticky.
    if (!isStickyHeaderEnabled()) return 0;

    var offset = Math.max(getMobileHeaderHeightPx(), getDesktopNavHeightPx());
    // Tope defensivo: nunca reservar de más (evita huecos).
    return Math.min(offset, 300);
  }

  function publishHeaderCoverVars() {
    var cover = getHeaderCoverPx();
    ROOT.style.setProperty('--chagual-header-cover', px(cover));
    ROOT.style.setProperty('--chagual-header-offset', px(cover));
    // Offset para scroll-padding-top (anclas/foco no tapados por el header).
    ROOT.style.setProperty('--chagual-scroll-offset', px(getScrollOffsetPx()));
    return cover;
  }

  function bindStandardLifecycle(handler) {
    document.addEventListener('DOMContentLoaded', handler, { passive: true });
    window.addEventListener('load', handler, { passive: true });
    window.addEventListener('pageshow', handler, { passive: true });
    document.addEventListener('shopify:section:load', handler);
    document.addEventListener('shopify:section:select', handler);
    document.addEventListener('turbo:load', handler);
    document.addEventListener('turbolinks:load', handler);
    document.addEventListener('spf:load', handler);
    document.addEventListener('spf:afterload', handler);
  }

  /* =========================================================
     A) Shared header cover publisher
     ========================================================= */

  function scheduleHeaderCoverBurst() {
    publishHeaderCoverVars();
    setTimeout(publishHeaderCoverVars, 0);
    setTimeout(publishHeaderCoverVars, 50);
    setTimeout(publishHeaderCoverVars, 150);
    setTimeout(publishHeaderCoverVars, 300);
    setTimeout(publishHeaderCoverVars, 600);
    setTimeout(publishHeaderCoverVars, 1000);
  }

  function bootHeaderCoverPublisher() {
    bindStandardLifecycle(scheduleHeaderCoverBurst);

    try {
      var headerEl = getHeaderEl();
      if (headerEl && 'ResizeObserver' in window) {
        var headerResizeObserver = new ResizeObserver(function () {
          publishHeaderCoverVars();
        });
        headerResizeObserver.observe(headerEl);
      }
    } catch (err) {}

    try {
      if ('MutationObserver' in window) {
        var headerMutationObserver = new MutationObserver(function () {
          publishHeaderCoverVars();
        });
        headerMutationObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
        headerMutationObserver.observe(ROOT, {
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
    } catch (err) {}

    scheduleHeaderCoverBurst();
  }

  /* =========================================================
     B) Collection offset bridge
     ========================================================= */

  var collectionOffsetActive = false;
  var collectionObservedSection = null;
  var collectionResizeObserver = null;
  var collectionSurfaceObserver = null;

  function getCollectionSection() {
    return document.querySelector(SELECTORS.collectionSection);
  }

  function getOwnedCollectionSurface(collectionSection) {
    if (!collectionSection || !collectionSection.querySelector) return null;

    for (var i = 0; i < SELECTORS.ownedCollectionSurfaces.length; i += 1) {
      var surface = collectionSection.querySelector(
        SELECTORS.ownedCollectionSurfaces[i]
      );
      if (surface) return surface;
    }

    return null;
  }

  function getCollectionOffsetSurface() {
    var collectionSection = getCollectionSection();
    var ownedSurface = getOwnedCollectionSurface(collectionSection);

    if (!collectionSection || !ownedSurface) return null;

    return {
      section: collectionSection,
      owner: ownedSurface
    };
  }

  function disconnectCollectionObserver(observer) {
    if (!observer || !observer.disconnect) return;

    try {
      observer.disconnect();
    } catch (err) {}
  }

  function deactivateCollectionOffsetBridge() {
    collectionOffsetActive = false;
    collectionObservedSection = null;
    disconnectCollectionObserver(collectionResizeObserver);
    disconnectCollectionObserver(collectionSurfaceObserver);
    collectionResizeObserver = null;
    collectionSurfaceObserver = null;
    ROOT.style.setProperty('--chagual-header-push', '0px');
  }

  function recalcCollectionOffset() {
    if (!collectionOffsetActive) return;

    var surface = getCollectionOffsetSurface();
    if (!surface || surface.section !== collectionObservedSection) {
      ensureCollectionOffsetBridge();
      return;
    }

    var cover = publishHeaderCoverVars();
    var sectionTop = surface.section.getBoundingClientRect().top || 0;
    var push = Math.max(0, cover - sectionTop);

    if (push > 200) push = cover;

    ROOT.style.setProperty('--chagual-header-push', px(push));
  }

  function scheduleCollectionOffsetBurst() {
    recalcCollectionOffset();
    setTimeout(recalcCollectionOffset, 0);
    setTimeout(recalcCollectionOffset, 50);
    setTimeout(recalcCollectionOffset, 150);
    setTimeout(recalcCollectionOffset, 300);
    setTimeout(recalcCollectionOffset, 600);
    setTimeout(recalcCollectionOffset, 1000);
  }

  function activateCollectionOffsetBridge(surface) {
    if (
      collectionOffsetActive &&
      collectionObservedSection === surface.section
    ) {
      return;
    }

    if (collectionOffsetActive) {
      deactivateCollectionOffsetBridge();
    }

    collectionOffsetActive = true;
    collectionObservedSection = surface.section;

    try {
      var headerEl = getHeaderEl();
      if (headerEl && 'ResizeObserver' in window) {
        collectionResizeObserver = new ResizeObserver(function () {
          recalcCollectionOffset();
        });
        collectionResizeObserver.observe(headerEl);
      }
    } catch (err) {}

    try {
      if ('MutationObserver' in window) {
        collectionSurfaceObserver = new MutationObserver(function () {
          recalcCollectionOffset();
        });
        collectionSurfaceObserver.observe(document.body, {
          childList: true,
          subtree: true
        });
      }
    } catch (err) {}

    scheduleCollectionOffsetBurst();
  }

  function ensureCollectionOffsetBridge() {
    var surface = getCollectionOffsetSurface();

    if (!surface) {
      if (collectionOffsetActive) {
        deactivateCollectionOffsetBridge();
      }
      return;
    }

    if (
      collectionOffsetActive &&
      collectionObservedSection === surface.section
    ) {
      scheduleCollectionOffsetBurst();
      return;
    }

    activateCollectionOffsetBridge(surface);
  }

  function bootCollectionOffsetLifecycle() {
    bindStandardLifecycle(ensureCollectionOffsetBridge);

    window.addEventListener('resize', function () {
      if (collectionOffsetActive) recalcCollectionOffset();
    }, { passive: true });

    window.addEventListener('orientationchange', function () {
      if (collectionOffsetActive) scheduleCollectionOffsetBurst();
    }, { passive: true });

    ensureCollectionOffsetBridge();
  }

  /* =========================================================
     C) PDP medidas anchor bridge
     ========================================================= */

  function hasPdpMedidasSurface() {
    return !!(
      document.querySelector(SELECTORS.pdpTarget) ||
      document.querySelector(SELECTORS.pdpLink) ||
      window.location.hash === '#pdp-medidas'
    );
  }

  function getHeaderCoverForScroll() {
    publishHeaderCoverVars();

    var raw = getComputedStyle(ROOT).getPropertyValue('--chagual-header-cover');
    var cover = parseFloat(String(raw || '').replace('px', ''));

    if (!isFinite(cover)) {
      cover = getHeaderCoverPx();
    }

    return isFinite(cover) ? cover : 0;
  }

  function scrollToTarget(target, hash) {
    try {
      if (target && target.tagName === 'DETAILS' && !target.open) {
        target.open = true;
      }
    } catch (err) {}

    var cover = getHeaderCoverForScroll();
    var extra = 12;
    var rect = target.getBoundingClientRect();
    var y = window.pageYOffset + rect.top - cover - extra;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });

    try {
      history.pushState(null, '', hash);
    } catch (err) {}
  }

  function onPdpMeasuresLinkClick(event) {
    var anchor = event.target && event.target.closest
      ? event.target.closest('a[href^="#"]')
      : null;

    if (!anchor) return;

    var hash = anchor.getAttribute('href') || '';
    if (hash.length < 2) return;

    var id = '';
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (err) {
      id = hash.slice(1);
    }

    if (!(id === 'pdp-medidas' || anchor.classList.contains('pdp-measures-inline__a'))) {
      return;
    }

    var target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    scrollToTarget(target, hash);
  }

  function bootPdpMeasuresAnchorBridge() {
    if (!hasPdpMedidasSurface()) return;

    document.addEventListener('click', onPdpMeasuresLinkClick, true);

    window.addEventListener('load', function () {
      if (window.location.hash !== '#pdp-medidas') return;

      var target = document.getElementById('pdp-medidas');
      if (!target) return;

      setTimeout(function () {
        scrollToTarget(target, '#pdp-medidas');
      }, 50);
    });
  }

  bootHeaderCoverPublisher();
  bootCollectionOffsetLifecycle();
  bootPdpMeasuresAnchorBridge();
})();
